import type { AnyFieldApi, AnyFormApi } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-form';
import { useQuestionBuilder } from 'lib-node-question-builder';
import { useEffect, useState } from 'react';

import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import Loan1003FormContainer from '~/pages/Loan1003DataGrids/common/components/Loan1003FormContainer';
import Loan1003FormSection from '~/pages/Loan1003DataGrids/common/components/Loan1003FormSection';

import { useBorrower1003Store } from '../../../borrower1003Store';
import { AssociatedAddresses } from './AssociatedAddresses';
import { MailingAddress } from './MailingAddress';
import { ResidencyHistory } from './ResidencyHistory';
import { ResidencyHistoryPrimaryAlerts } from './ResidencyHistoryPrimaryAlerts';
import { useAddressInfoFormData, useAddressInfoMutation } from './hooks';
import {
  SAME_AS_PRIMARY_FIELD,
  addressCountryListener,
  addressStateListener,
  addressZipCodeListener,
  primaryAddressCastToBooleanListener,
  primaryResidencePreviousListener,
  sameAsPrimaryListener,
  syncMailingAddressFromPrimaryListener,
} from './listeners';
import { clearInitialPrimaryId } from './primaryAddressSelection';

type AddressInfoFormProps = {
  borrowerId: number;
};

function handleAddressInfoFieldChange({
  fieldApi,
  formApi,
  isPrimaryAddressSelectionEnabled,
}: {
  fieldApi: AnyFieldApi;
  formApi: AnyFormApi;
  isPrimaryAddressSelectionEnabled: boolean;
}) {
  if (fieldApi.name.endsWith('.country')) {
    addressCountryListener(fieldApi, formApi);
  }

  if (fieldApi.name.endsWith('.state')) {
    addressStateListener(fieldApi, formApi);
  }

  if (fieldApi.name.endsWith('.zipCode')) {
    addressZipCodeListener(fieldApi, formApi);
  }

  if (fieldApi.name === SAME_AS_PRIMARY_FIELD) {
    sameAsPrimaryListener(fieldApi, formApi);
  }

  if (fieldApi.name.endsWith('.borrowerAddress.primaryResidence')) {
    if (isPrimaryAddressSelectionEnabled) {
      primaryResidencePreviousListener(fieldApi, formApi);
    }
    primaryAddressCastToBooleanListener(fieldApi, formApi);
  }

  syncMailingAddressFromPrimaryListener(
    fieldApi,
    formApi,
    isPrimaryAddressSelectionEnabled,
  );
}

export function AddressInfoForm(props: Readonly<AddressInfoFormProps>) {
  const isWriteAccessDisabled = useIsWriteAccessDisabled();

  const { data, isLoading } = useAddressInfoFormData(props.borrowerId);

  const isPrimaryAddressSelectionEnabled = Boolean(
    data?.isPrimaryAddressSelectionEnabled,
  );
  const [initialPrimaryId, setInitialPrimaryId] = useState<number | null>(null);

  useEffect(() => {
    setInitialPrimaryId(
      isPrimaryAddressSelectionEnabled
        ? (data?.initialPrimaryAddressId ?? null)
        : null,
    );
  }, [
    data?.initialPrimaryAddressId,
    isPrimaryAddressSelectionEnabled,
    props.borrowerId,
  ]);

  const handleAddressDeleted = (addressId: number | null) => {
    setInitialPrimaryId((currentInitialPrimaryId) =>
      clearInitialPrimaryId(addressId, currentInitialPrimaryId),
    );
  };

  const { mutate, isLoading: isMutating } = useAddressInfoMutation(
    props.borrowerId,
  );

  const { form, handleSubmit, formApi } = useQuestionBuilder({
    formData: data?.formData,
    screenData: {
      isWriteAccessDisabled: isWriteAccessDisabled,
    },
    hooks: {
      onSubmit: (values) => {
        mutate(values);
      },
    },
    listeners: {
      onChange: (fieldChange) => {
        handleAddressInfoFieldChange({
          ...fieldChange,
          isPrimaryAddressSelectionEnabled: Boolean(
            data?.isPrimaryAddressSelectionEnabled,
          ),
        });
      },
    },
  });

  const isSaveButtonDisabled = useStore(formApi.store, (s) => {
    const primaryResidences =
      s.values?.residenceHistoryDetails?.borrowerResidenceHistory?.filter(
        ({ borrowerAddress }) => borrowerAddress?.primaryResidence === true,
      );

    const primaryCount =
      (s.values?.residenceHistoryDetails?.primaryResidence ? 1 : 0) +
      (primaryResidences?.length ?? 0);

    return primaryCount === 0 || primaryCount > 1;
  });

  return (
    <form.Provider>
      <Loan1003FormContainer
        isLoading={isLoading || isMutating}
        disableSave={isSaveButtonDisabled}
        readOnlyMode={isWriteAccessDisabled}
        onCloseClicked={() => {
          useBorrower1003Store.setState({ selectedBorrowerId: null });
        }}
        onSaveClicked={() => {
          handleSubmit();
        }}
      >
        <Loan1003FormSection title="Residency History">
          <ResidencyHistoryPrimaryAlerts form={form} />
          <ResidencyHistory
            form={form}
            dropdownOptions={data?.dropdownOptions}
            isPrimaryAddressSelectionEnabled={isPrimaryAddressSelectionEnabled}
            primaryAddressSelectionOptions={
              data?.primaryAddressSelectionOptions
            }
            initialPrimaryId={initialPrimaryId}
            onAddressDeleted={handleAddressDeleted}
          />
        </Loan1003FormSection>
        <Loan1003FormSection title="Associated Addresses">
          <AssociatedAddresses
            form={form}
            onAddressDeleted={handleAddressDeleted}
          />
        </Loan1003FormSection>
        <Loan1003FormSection title="Mailing Address">
          <MailingAddress
            form={form}
            defaultWholeAddressString={
              data?.formData?.residenceHistoryDetails?.mailingAddress
                ?.wholeAddressString ?? ''
            }
          />
        </Loan1003FormSection>
      </Loan1003FormContainer>
    </form.Provider>
  );
}
